import { useEffect, useMemo, useState } from "react";
import toast from "../../utils/toast";
import axios from "../../lib/axios";
import { BACKEND_URL } from "../../config";

const verificationStatuses = [
  "ALL",
  "DISCOVERED",
  "USABLE",
  "PENDING_REVIEW",
  "VERIFIED",
  "CLAIMED",
  "PUBLISHED",
  "REJECTED",
  "DUPLICATE",
];

const sourceTypes = [
  "",
  "GOOGLE_MAPS",
  "JUSTDIAL",
  "SULEKHA",
  "AAEC",
  "UNIVERSITY_AGENT_LIST",
  "COMPANY_WEBSITE",
  "SOCIAL_MEDIA",
  "MANUAL",
  "OWNER_CLAIM",
  "OTHER",
];

const validationStatuses = [
  "",
  "VALID",
  "VALID_INCOMPLETE",
  "NEEDS_REVIEW",
  "POSSIBLE_DUPLICATE",
  "POSSIBLY_CLOSED",
  "INVALID",
  "NOT_STUDY_ABROAD",
  "TEST_DATA",
];

export default function AdminConsultancies() {
  const [consultancies, setConsultancies] = useState([]);
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState({
    search: "",
    verificationStatus: "ALL",
    state: "",
    city: "",
    source: "",
    claimed: "",
    published: "",
    minScore: "",
    validationStatus: "",
  });
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [branchForm, setBranchForm] = useState({
    branchName: "",
    address: "",
    city: "",
    state: "",
    pincode: "",
    phone: "",
    email: "",
    website: "",
  });
  const [csvText, setCsvText] = useState("");
  const [preview, setPreview] = useState(null);
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const token = localStorage.getItem("token");
  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchStats = async () => {
    const res = await axios.get(`${BACKEND_URL}/admin/consultancies/stats`, {
      headers: authHeaders,
    });
    setStats(res.data);
  };

  const fetchConsultancies = async (override = {}) => {
    setLoading(true);
    try {
      const params = { ...filters, ...override };
      Object.keys(params).forEach((key) => {
        if (!params[key] || params[key] === "ALL") delete params[key];
      });
      const res = await axios.get(`${BACKEND_URL}/admin/consultancies`, {
        params,
        headers: authHeaders,
      });
      setConsultancies(res.data || []);
      setPage(1);
      fetchStats();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || "Failed to load consultancies");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConsultancies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateFilter = (key, value) => {
    setFilters((current) => ({ ...current, [key]: value }));
  };

  const workflowAction = async (id, action, body = {}) => {
    try {
      await axios.post(`${BACKEND_URL}/admin/consultancies/${id}/${action}`, body, {
        headers: authHeaders,
      });
      toast.success(`Consultancy ${action} updated`);
      fetchConsultancies();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || "Action failed");
    }
  };

  const openDetails = async (consultancy) => {
    setSelected(consultancy);
    setDetailLoading(true);
    setBranchForm({
      branchName: "",
      address: "",
      city: "",
      state: "",
      pincode: "",
      phone: "",
      email: "",
      website: "",
    });
    try {
      const res = await axios.get(`${BACKEND_URL}/admin/consultancy/${consultancy._id}`, {
        headers: authHeaders,
      });
      setSelected(res.data);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || "Failed to load consultancy details");
    } finally {
      setDetailLoading(false);
    }
  };

  const handleMerge = async (id) => {
    const targetConsultancyId = window.prompt("Target consultancy ID to merge into");
    if (!targetConsultancyId) return;
    await workflowAction(id, "merge", { targetConsultancyId });
  };

  const addBranch = async () => {
    if (!selected?._id) return;
    if (!branchForm.city && !branchForm.address) {
      toast.error("Branch city or address is required");
      return;
    }
    try {
      const res = await axios.post(
        `${BACKEND_URL}/admin/consultancies/${selected._id}/branches`,
        branchForm,
        { headers: authHeaders }
      );
      toast.success("Branch added");
      setSelected((current) => ({
        ...current,
        branches: [...(current.branches || []), res.data.branch],
      }));
      setBranchForm({
        branchName: "",
        address: "",
        city: "",
        state: "",
        pincode: "",
        phone: "",
        email: "",
        website: "",
      });
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || "Failed to add branch");
    }
  };

  const deleteBranch = async (branchId) => {
    if (!selected?._id) return;
    if (!window.confirm("Delete this branch?")) return;
    try {
      await axios.delete(`${BACKEND_URL}/admin/consultancies/${selected._id}/branches/${branchId}`, {
        headers: authHeaders,
      });
      toast.success("Branch deleted");
      setSelected((current) => ({
        ...current,
        branches: (current.branches || []).filter((branch) => branch._id !== branchId),
      }));
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || "Failed to delete branch");
    }
  };

  const handleValidateCsv = async () => {
    try {
      const res = await axios.post(
        `${BACKEND_URL}/admin/consultancies/validate`,
        { csv: csvText },
        { headers: authHeaders }
      );
      setPreview(res.data);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || "CSV validation failed");
    }
  };

  const handleImportCsv = async () => {
    try {
      const res = await axios.post(
        `${BACKEND_URL}/admin/consultancies/import`,
        { csv: csvText },
        { headers: authHeaders }
      );
      toast.success(`Imported ${res.data.importedCount} rows, skipped ${res.data.skippedCount}`);
      setPreview(null);
      fetchConsultancies();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || "CSV import failed");
    }
  };

  const handleExport = async (exportType = "") => {
    try {
      const res = await axios.get(`${BACKEND_URL}/admin/consultancies/export`, {
        params: exportType ? { exportType } : filters,
        headers: authHeaders,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `consultancies_${exportType || "filtered"}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      toast.error("Export failed");
    }
  };

  const totalPages = Math.max(1, Math.ceil(consultancies.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const paged = consultancies.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
        <div>
          <p className="text-sm uppercase tracking-[5px] text-white/50">Consultancy Database</p>
          <h2 className="text-2xl font-semibold">Discovery, verification, and publication</h2>
          <p className="mt-1 text-sm text-white/60">
            Records stay unpublished until they pass review. Source tracking is visible for every imported listing.
          </p>
        </div>
        {stats && (
          <div className="w-full max-w-md rounded-2xl border border-white/10 bg-white/5 p-4">
            <div className="flex items-center justify-between text-sm">
              <span className="text-white/60">Verified target</span>
              <span>{stats.totalVerified} / {stats.target}</span>
            </div>
            <div className="mt-3 h-2 rounded-full bg-white/10">
              <div
                className="h-2 rounded-full bg-emerald-400"
                style={{ width: `${Math.min(stats.percentage, 100)}%` }}
              />
            </div>
          </div>
        )}
      </header>

      {stats?.validation && (
        <section className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
          {[
            ["Validated", `${stats.validation.totalValidated || 0} / ${stats.totalDiscovered || 0}`],
            ["Valid", stats.validation.valid || 0],
            ["Valid incomplete", stats.validation.validIncomplete || 0],
            ["Needs review", stats.validation.needsReview || 0],
            ["Duplicates", stats.validation.possibleDuplicates || 0],
            ["Possibly closed", stats.validation.possiblyClosed || 0],
            ["Invalid", stats.validation.invalid || 0],
            ["Missing countries", stats.validation.missingProcessingCountries || 0],
            ["Missing sources", stats.validation.missingSources || 0],
            ["Avg score", stats.validation.averageScore || 0],
            ["High confidence", stats.validation.highConfidence || 0],
            ["Medium confidence", stats.validation.mediumConfidence || 0],
            ["Low confidence", stats.validation.lowConfidence || 0],
          ].map(([label, value]) => (
            <div key={label} className="rounded-2xl border border-white/10 bg-white/5 p-3">
              <p className="text-xl font-semibold">{value}</p>
              <p className="mt-1 text-xs uppercase tracking-[2px] text-white/50">{label}</p>
            </div>
          ))}
        </section>
      )}

      <section className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        <input
          value={filters.search}
          onChange={(e) => updateFilter("search", e.target.value)}
          placeholder="Search name, phone, email, website, city"
          className="rounded-2xl border border-white/10 bg-white/5 px-4 py-2 text-sm text-white placeholder-white/50"
        />
        <select
          value={filters.verificationStatus}
          onChange={(e) => updateFilter("verificationStatus", e.target.value)}
          className="rounded-2xl border border-white/10 bg-white/5 px-4 py-2 text-sm text-white"
        >
          {verificationStatuses.map((status) => (
            <option key={status} className="bg-slate-900" value={status}>{status}</option>
          ))}
        </select>
        <input
          value={filters.state}
          onChange={(e) => updateFilter("state", e.target.value)}
          placeholder="State"
          className="rounded-2xl border border-white/10 bg-white/5 px-4 py-2 text-sm text-white placeholder-white/50"
        />
        <input
          value={filters.city}
          onChange={(e) => updateFilter("city", e.target.value)}
          placeholder="City"
          className="rounded-2xl border border-white/10 bg-white/5 px-4 py-2 text-sm text-white placeholder-white/50"
        />
        <select
          value={filters.source}
          onChange={(e) => updateFilter("source", e.target.value)}
          className="rounded-2xl border border-white/10 bg-white/5 px-4 py-2 text-sm text-white"
        >
          {sourceTypes.map((source) => (
            <option key={source || "all"} className="bg-slate-900" value={source}>
              {source || "All sources"}
            </option>
          ))}
        </select>
        <select
          value={filters.claimed}
          onChange={(e) => updateFilter("claimed", e.target.value)}
          className="rounded-2xl border border-white/10 bg-white/5 px-4 py-2 text-sm text-white"
        >
          <option className="bg-slate-900" value="">Claimed or unclaimed</option>
          <option className="bg-slate-900" value="true">Claimed</option>
          <option className="bg-slate-900" value="false">Unclaimed</option>
        </select>
        <select
          value={filters.published}
          onChange={(e) => updateFilter("published", e.target.value)}
          className="rounded-2xl border border-white/10 bg-white/5 px-4 py-2 text-sm text-white"
        >
          <option className="bg-slate-900" value="">Published or unpublished</option>
          <option className="bg-slate-900" value="true">Published</option>
          <option className="bg-slate-900" value="false">Unpublished</option>
        </select>
        <input
          value={filters.minScore}
          onChange={(e) => updateFilter("minScore", e.target.value)}
          placeholder="Min data score"
          className="rounded-2xl border border-white/10 bg-white/5 px-4 py-2 text-sm text-white placeholder-white/50"
        />
        <select
          value={filters.validationStatus}
          onChange={(e) => updateFilter("validationStatus", e.target.value)}
          className="rounded-2xl border border-white/10 bg-white/5 px-4 py-2 text-sm text-white"
        >
          {validationStatuses.map((status) => (
            <option key={status || "all"} className="bg-slate-900" value={status}>
              {status || "Validation status"}
            </option>
          ))}
        </select>
        <div className="flex flex-wrap gap-2 xl:col-span-4">
          <button onClick={() => fetchConsultancies()} className="rounded-2xl bg-white/10 px-4 py-2 text-sm hover:bg-white/20">
            Apply
          </button>
          <button
            onClick={() => {
              const reset = { search: "", verificationStatus: "ALL", state: "", city: "", source: "", claimed: "", published: "", minScore: "", validationStatus: "" };
              setFilters(reset);
              fetchConsultancies(reset);
            }}
            className="rounded-2xl border border-white/10 px-4 py-2 text-sm text-white/80 hover:text-white"
          >
            Reset
          </button>
          {["all", "verified", "usable", "pending", "unclaimed"].map((type) => (
            <button
              key={type}
              onClick={() => handleExport(type === "all" ? "" : type)}
              className="rounded-2xl border border-white/10 px-4 py-2 text-sm text-white/80 hover:bg-white/10"
            >
              Export {type}
            </button>
          ))}
        </div>
      </section>

      <section className="rounded-3xl border border-white/10 bg-white/5 p-4">
        <h3 className="text-lg font-semibold">CSV Import</h3>
        <p className="mt-1 text-xs text-white/60">
          Expected columns: name, phone, email, website, address, city, state, pincode, countries, services, sourceName, sourceUrl, sourceType, socialLinks.
        </p>
        <textarea
          value={csvText}
          onChange={(e) => setCsvText(e.target.value)}
          rows={5}
          className="mt-3 w-full rounded-2xl border border-white/10 bg-black/20 p-3 text-sm text-white placeholder-white/40"
          placeholder="Paste CSV content here for validation preview"
        />
        <div className="mt-3 flex flex-wrap gap-2">
          <button onClick={handleValidateCsv} className="rounded-2xl bg-white/10 px-4 py-2 text-sm hover:bg-white/20">
            Validate
          </button>
          <button
            onClick={handleImportCsv}
            disabled={!preview}
            className="rounded-2xl border border-emerald-400/30 px-4 py-2 text-sm text-emerald-200 disabled:opacity-40"
          >
            Import all valid
          </button>
        </div>
        {preview && (
          <div className="mt-4 grid gap-3 text-sm md:grid-cols-5">
            {[
              ["Total", preview.totalRows],
              ["Valid", preview.validRows],
              ["Invalid", preview.invalidRows],
              ["Duplicates", preview.possibleDuplicates],
              ["New", preview.newConsultancies],
            ].map(([label, value]) => (
              <div key={label} className="rounded-2xl border border-white/10 bg-black/20 p-3">
                <p className="text-xl font-semibold">{value}</p>
                <p className="text-white/50">{label}</p>
              </div>
            ))}
          </div>
        )}
      </section>

      <div className="overflow-x-auto rounded-3xl border border-white/10 bg-white/5">
        <table className="min-w-[1200px] w-full text-left text-sm">
          <thead className="text-xs uppercase tracking-[2px] text-white/50">
            <tr>
              {["Name", "City", "State", "Phone", "Website", "Countries", "Data Score", "Validation", "Status", "Source", "Claimed", "Last Verified", "Actions"].map((header) => (
                <th key={header} className="px-4 py-3 font-medium">{header}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {loading && (
              <tr><td colSpan="13" className="px-4 py-8 text-center text-white/60">Loading consultancies...</td></tr>
            )}
            {!loading && paged.length === 0 && (
              <tr><td colSpan="13" className="px-4 py-8 text-center text-white/60">No consultancies found.</td></tr>
            )}
            {!loading && paged.map((consultancy) => (
              <tr key={consultancy._id} className="align-top hover:bg-white/5">
                <td className="px-4 py-3">
                  <button onClick={() => openDetails(consultancy)} className="font-semibold text-left hover:text-blue-200">
                    {consultancy.name}
                  </button>
                  <p className="text-xs text-white/40">{consultancy._id}</p>
                </td>
                <td className="px-4 py-3">{consultancy.city || "-"}</td>
                <td className="px-4 py-3">{consultancy.state || "-"}</td>
                <td className="px-4 py-3">{consultancy.phone || "-"}</td>
                <td className="px-4 py-3">{consultancy.website || "-"}</td>
                <td className="px-4 py-3">{(consultancy.countries || []).join(", ") || "-"}</td>
                <td className="px-4 py-3">{consultancy.dataCompletenessScore}</td>
                <td className="px-4 py-3">
                  {consultancy.validationAudit ? (
                    <div>
                      <span className="rounded-full border border-cyan-300/30 bg-cyan-300/10 px-3 py-1 text-xs text-cyan-100">
                        {consultancy.validationAudit.status}
                      </span>
                      <p className="mt-1 text-xs text-white/50">
                        {consultancy.validationAudit.score}/100 · {consultancy.validationAudit.confidence}
                      </p>
                    </div>
                  ) : "-"}
                </td>
                <td className="px-4 py-3">
                  <span className="rounded-full border border-white/10 bg-black/20 px-3 py-1 text-xs">
                    {consultancy.verificationStatus}
                  </span>
                </td>
                <td className="px-4 py-3">{consultancy.sourceType || consultancy.sourceName || "-"}</td>
                <td className="px-4 py-3">{consultancy.claimed ? "Yes" : "No"}</td>
                <td className="px-4 py-3">
                  {consultancy.lastVerified ? new Date(consultancy.lastVerified).toLocaleDateString() : "-"}
                </td>
                <td className="px-4 py-3">
                  <div className="flex max-w-xs flex-wrap gap-2">
                    <button onClick={() => openDetails(consultancy)} className="rounded-xl border border-white/10 px-3 py-1 hover:bg-white/10">View</button>
                    <button onClick={() => workflowAction(consultancy._id, "verify")} className="rounded-xl border border-emerald-400/30 px-3 py-1 text-emerald-200">Verify</button>
                    <button onClick={() => workflowAction(consultancy._id, "unverify")} className="rounded-xl border border-white/10 px-3 py-1">Unverify</button>
                    <button onClick={() => workflowAction(consultancy._id, "publish")} className="rounded-xl border border-blue-400/30 px-3 py-1 text-blue-200">Publish</button>
                    <button onClick={() => workflowAction(consultancy._id, "unpublish")} className="rounded-xl border border-white/10 px-3 py-1">Unpublish</button>
                    <button onClick={() => workflowAction(consultancy._id, "duplicate")} className="rounded-xl border border-amber-400/30 px-3 py-1 text-amber-200">Duplicate</button>
                    <button onClick={() => handleMerge(consultancy._id)} className="rounded-xl border border-purple-400/30 px-3 py-1 text-purple-200">Merge</button>
                    <button onClick={() => workflowAction(consultancy._id, "reject")} className="rounded-xl border border-rose-400/30 px-3 py-1 text-rose-200">Reject</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {totalPages > 1 && (
          <div className="flex items-center justify-end gap-3 px-4 py-3 text-sm">
            <button disabled={currentPage === 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="rounded-xl border border-white/10 px-3 py-1 disabled:opacity-40">
              Prev
            </button>
            <span>Page {currentPage} / {totalPages}</span>
            <button disabled={currentPage === totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} className="rounded-xl border border-white/10 px-3 py-1 disabled:opacity-40">
              Next
            </button>
          </div>
        )}
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
          <div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-3xl border border-white/10 bg-slate-950 p-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="text-2xl font-semibold">{selected.name}</h3>
                <p className="text-sm text-white/60">{selected.verificationStatus} · {selected.claimStatus}</p>
              </div>
              <button onClick={() => setSelected(null)} className="text-white/60 hover:text-white">Close</button>
            </div>
            {detailLoading && (
              <div className="mt-6 rounded-2xl border border-white/10 bg-white/5 p-4 text-sm text-white/60">
                Loading full record...
              </div>
            )}
            <div className="mt-6 grid gap-4 text-sm md:grid-cols-2">
              <Info label="Email" value={selected.email} />
              <Info label="Phone" value={selected.phone} />
              <Info label="Website" value={selected.website} />
              <Info label="Location" value={[selected.city, selected.state].filter(Boolean).join(", ")} />
              <Info label="Source" value={[selected.sourceName, selected.sourceType].filter(Boolean).join(" · ")} />
              <Info label="Data score" value={selected.dataCompletenessScore} />
              <Info label="Validation status" value={selected.validationAudit?.status} />
              <Info label="Validation score" value={selected.validationAudit ? `${selected.validationAudit.score}/100` : ""} />
              <Info label="Validation confidence" value={selected.validationAudit?.confidence} />
              <Info label="Recommended action" value={selected.validationAudit?.recommendedAction} />
              <Info label="Study abroad confirmed" value={selected.validationAudit?.studyAbroadConfirmed ? "Yes" : "No"} />
              <Info label="Website status" value={selected.validationAudit?.websiteStatus} />
              <Info label="Source status" value={selected.validationAudit?.sourceStatus} />
              <Info label="Duplicate score" value={selected.duplicateScore} />
              <Info label="Published" value={selected.published ? "Yes" : "No"} />
            </div>
            <div className="mt-6 rounded-2xl border border-white/10 bg-white/5 p-4">
              <div className="flex items-center justify-between gap-4">
                <h4 className="font-semibold">Branches</h4>
                <span className="text-sm text-white/50">{(selected.branches || []).length} listed</span>
              </div>
              <div className="mt-4 space-y-3">
                {(selected.branches || []).map((branch) => (
                  <div key={branch._id} className="rounded-2xl border border-white/10 bg-black/20 p-3 text-sm">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-semibold">{branch.branchName || branch.city || "Branch"}</p>
                        <p className="mt-1 text-white/70">{branch.address || "-"}</p>
                        <p className="mt-1 text-white/50">
                          {[branch.city, branch.state, branch.pincode].filter(Boolean).join(", ") || "-"}
                        </p>
                      </div>
                      <button
                        onClick={() => deleteBranch(branch._id)}
                        className="rounded-xl border border-rose-400/30 px-3 py-1 text-rose-200"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
                {!selected.branches?.length && (
                  <p className="text-sm text-white/50">No branches added yet.</p>
                )}
              </div>
              <div className="mt-5 grid gap-3 md:grid-cols-2">
                {[
                  ["branchName", "Branch name"],
                  ["city", "City"],
                  ["state", "State"],
                  ["pincode", "PIN code"],
                  ["phone", "Phone"],
                  ["email", "Email"],
                  ["website", "Website"],
                  ["address", "Address"],
                ].map(([key, label]) => (
                  <input
                    key={key}
                    value={branchForm[key]}
                    onChange={(event) => setBranchForm((current) => ({ ...current, [key]: event.target.value }))}
                    placeholder={label}
                    className="rounded-2xl border border-white/10 bg-black/20 px-3 py-2 text-sm text-white placeholder-white/40"
                  />
                ))}
              </div>
              <button
                onClick={addBranch}
                className="mt-3 rounded-2xl border border-emerald-400/30 px-4 py-2 text-sm text-emerald-200"
              >
                Add branch
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-3">
      <p className="text-xs uppercase tracking-[3px] text-white/40">{label}</p>
      <p className="mt-1 break-words">{value || "-"}</p>
    </div>
  );
}
